import { Colors, CalendarIcon, tokens } from '@gripp/shared-ui';
import { Pressable, StyleSheet, View } from 'react-native';
import { Path, Svg } from 'react-native-svg';

export type ActivityView = 'list' | 'calendar';

interface ActivityViewSelectorProps {
  view: ActivityView;
  onChange: (view: ActivityView) => void;
}

/**
 * List ↔ calendar view selector for the Activity screen (plan 0017 §1, ported
 * from gripp-OS `ActivityPage.tsx`). A pill container with two 24×24 icon
 * buttons; the active button gets the inverse (#020202) background and a light
 * icon (#FCFCFC), the inactive one a dark icon (#020202).
 *
 * The list glyph is drawn as three horizontal lines, matching gripp-OS's 3-line
 * SVG (15px box, lines spanning ~10/16 of the width) — there is no equivalent
 * Crop Circle icon, so it is rendered inline. The calendar glyph is the Crop
 * Circle `CalendarIcon`.
 */
export const ActivityViewSelector = ({
  view,
  onChange,
}: ActivityViewSelectorProps) => {
  const listActive = view === 'list';
  const calendarActive = view === 'calendar';

  return (
    <View style={styles.container}>
      <Pressable
        onPress={() => onChange('list')}
        style={[styles.button, listActive && styles.buttonActive]}
        accessibilityRole="button"
        accessibilityState={{ selected: listActive }}
        accessibilityLabel="List view"
      >
        <Svg
          width={15}
          height={15}
          viewBox="0 0 16 16"
          fill="none"
          stroke={listActive ? ICON_ACTIVE : ICON_INACTIVE}
          strokeWidth={1.6}
          strokeLinecap="round"
        >
          <Path d="M3 4h10M3 8h10M3 12h10" />
        </Svg>
      </Pressable>

      <Pressable
        onPress={() => onChange('calendar')}
        style={[styles.button, calendarActive && styles.buttonActive]}
        accessibilityRole="button"
        accessibilityState={{ selected: calendarActive }}
        accessibilityLabel="Calendar view"
      >
        <CalendarIcon
          size={12}
          color={calendarActive ? ICON_ACTIVE : ICON_INACTIVE}
        />
      </Pressable>
    </View>
  );
};

// §1: the active icon sits on the inverse pill and the inactive one on the
// soft pill, so each pairs with the role token its own background uses and the
// two invert together with the theme.
const ICON_ACTIVE = Colors.role.textOnInverse;
const ICON_INACTIVE = Colors.role.textStrong;

const styles = StyleSheet.create({
  // Pill container: bg surface-inverse-soft (#ECEEF1 = slate[100]), radius 99,
  // padding 2, gap 2, height 28 (§1).
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 28,
    padding: 2,
    gap: 2,
    borderRadius: tokens.borderRadius.pill, // 99
    backgroundColor: Colors.role.surfaceInverseSoft,
  },
  // Each icon button: 24×24, radius 99, centered icon (§1).
  button: {
    width: 24,
    height: 24,
    borderRadius: tokens.borderRadius.pill, // 99
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonActive: {
    backgroundColor: Colors.role.surfaceInverse,
  },
});
